// Local (on-device) dose reminder notifications for the Capacitor app.
// Scheduled from the same reminder data the Reminders screen shows, so the
// app fires 9 AM / 1 PM / 9 PM notifications even offline. WhatsApp reminders
// from the server cron are a separate, untouched channel.
import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import { TextToSpeech } from '@capacitor-community/text-to-speech'

// Never let a wedged native bridge call hang the UI forever.
const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))])

const SLOTS = {
  morning:   { hour: 9,  minute: 0, icon: '🌅', label: 'Morning' },
  afternoon: { hour: 13, minute: 0, icon: '☀️', label: 'Afternoon' },
  night:     { hour: 21, minute: 0, icon: '🌙', label: 'Night' },
}
const DAYS_AHEAD = 7
const CHANNEL_ID = 'medicine-reminders'
const OPEN_FLAG = 'revive_open_reminders'

const localISO = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const VOICE_PREF = 'revive_voice_reminders' // '1' = on (default), '0' = off
export const isVoiceOn = () => { try { return localStorage.getItem(VOICE_PREF) !== '0' } catch { return true } }
export const setVoiceOn = on => { try { localStorage.setItem(VOICE_PREF, on ? '1' : '0') } catch {} }

// Repeat-nudge frequency within each 1-hour dose window: 'off' | '15' | '30'.
// 'off' = one reminder at slot time; '30' = +2 nudges; '15' = +4 nudges.
const FREQ_PREF = 'revive_reminder_freq'
export const getReminderFreq = () => { try { return localStorage.getItem(FREQ_PREF) || 'off' } catch { return 'off' } }
export const setReminderFreq = f => { try { localStorage.setItem(FREQ_PREF, f) } catch {} }
const freqOffsets = f => f === '15' ? [0, 15, 30, 45] : f === '30' ? [0, 30] : [0] // minutes after slot open

// Speak the reminder out loud using the phone's built-in text-to-speech.
// Fully on-device — no server, no telephony, no cost. Web falls back to the
// browser SpeechSynthesis API so it also works in a desktop preview.
export async function speakReminder(text) {
  if (!text || !isVoiceOn()) return
  try {
    if (Capacitor.isNativePlatform()) {
      await TextToSpeech.speak({ text, lang: 'en-IN', rate: 0.95, pitch: 1.0, volume: 1.0, category: 'playback' })
    } else if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel()
      const u = new SpeechSynthesisUtterance(text)
      u.lang = 'en-IN'; u.rate = 0.95
      window.speechSynthesis.speak(u)
    }
  } catch (e) { console.warn('voice reminder:', e?.message || e) }
}

// Build the spoken line for the slot due right now (or nearest upcoming today).
// `name` (patient's first name) personalises the greeting to match the server.
export function voiceLineForNow(reminders, takenState = {}, name = '') {
  if (!Array.isArray(reminders)) return ''
  const now = new Date()
  const iso = localISO(now)
  const hour = now.getHours()
  const first = String(name || '').trim().split(/\s+/)[0]
  // Which slot are we in / closest to?
  const order = ['morning', 'afternoon', 'night']
  const pick = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'night'
  for (const type of [pick, ...order.filter(t => t !== pick)]) {
    const due = reminders.filter(r => r.date === iso && r.medicationType === type && !takenState[r.id]?.taken)
    if (due.length) {
      const label = SLOTS[type].label.toLowerCase()
      return first
        ? `Hi ${first}, it's time to take your ${label} medicines. Open the Revive app to mark them as taken.`
        : `It's time to take your ${label} medicines. Open the Revive app to mark them as taken.`
    }
  }
  return ''
}

function getPlugin() {
  return Capacitor.isNativePlatform() ? LocalNotifications : null
}

// Android 12+: without the "Alarms & reminders" special access, scheduled
// alarms become INEXACT and Android can silently drop/delay them during
// daytime Doze — the usual reason morning/afternoon reminders never arrive
// while evening ones (phone in active use) do. Returns 'granted' elsewhere.
export async function checkExactAlarms() {
  const LN = getPlugin()
  if (!LN || !LN.checkExactNotificationSetting) return 'granted'
  try { const r = await withTimeout(LN.checkExactNotificationSetting(), 8000); return r?.exact_alarm || 'granted' }
  catch { return 'granted' }
}

// Deep-link the user to the system "Alarms & reminders" screen for this app.
export async function requestExactAlarms() {
  const LN = getPlugin()
  if (!LN || !LN.changeExactNotificationSetting) return 'granted'
  try { const r = await withTimeout(LN.changeExactNotificationSetting(), 8000); return r?.exact_alarm || 'granted' }
  catch { return 'denied' }
}

// Cancel everything we scheduled and rebuild from current reminder data.
// Idempotent — safe to call on every app open and after every mark-taken.
export async function syncReminderNotifications(reminders, takenState = {}) {
  const LN = await getPlugin()
  if (!LN || !Array.isArray(reminders) || reminders.length === 0) return 0
  try {
    // Every native call is wrapped in withTimeout — without this a single wedged
    // bridge call left the whole re-sync hanging on "Scheduling…" and scheduled 0.
    let perm = await withTimeout(LN.checkPermissions(), 8000)
    if (perm.display !== 'granted') perm = await withTimeout(LN.requestPermissions(), 20000)
    if (perm.display !== 'granted') return 0

    try { await withTimeout(LN.createChannel({ id: CHANNEL_ID, name: 'Medicine Reminders', description: 'Dose reminders for your treatment plan', importance: 4, visibility: 1 }), 8000) } catch {}

    try {
      const pending = await withTimeout(LN.getPending(), 8000)
      if (pending?.notifications?.length) await withTimeout(LN.cancel({ notifications: pending.notifications.map(n => ({ id: n.id })) }), 8000)
    } catch {}

    const now = new Date()
    const offsets = freqOffsets(getReminderFreq())
    const toSchedule = []
    for (let d = 0; d < DAYS_AHEAD; d++) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + d)
      const iso = localISO(day)
      Object.entries(SLOTS).forEach(([type, s], si) => {
        const due = reminders.filter(r => r.date === iso && r.medicationType === type && !takenState[r.id]?.taken)
        if (!due.length) return
        // Base reminder at slot open, plus repeat nudges through the hour window
        offsets.forEach((off, ri) => {
          const at = new Date(day)
          at.setHours(s.hour, s.minute + off, 0, 0)
          if (at <= now) return
          toSchedule.push({
            id: 5000 + d * 100 + si * 10 + ri,
            title: `${s.icon} ${s.label} medicines${ri > 0 ? ' (reminder)' : ''}`,
            body: `Time to take your ${s.label.toLowerCase()} medicines — tap to mark as taken`,
            schedule: { at, allowWhileIdle: true },
            channelId: CHANNEL_ID,
            extra: { openReminders: true },
          })
        })
      })
    }
    // Schedule ONE AT A TIME — several Samsung/OEM builds only register the first
    // notification when a batch of exact alarms is scheduled in a single call.
    let done = 0
    for (const notif of toSchedule) {
      try { await withTimeout(LN.schedule({ notifications: [notif] }), 6000); done++ } catch {}
    }
    return done
  } catch (e) { console.warn('reminder notifications:', e?.message || e); return 0 }
}

// Snapshot of everything that has to be true for reminders to fire, so the
// Reminders screen can show the patient (and us) exactly what's blocking them.
export async function reminderDebugInfo() {
  const info = { native: Capacitor.isNativePlatform(), permission: 'n/a', exact: 'n/a', pending: 0 }
  const LN = getPlugin()
  if (!LN) return info
  try { const p = await withTimeout(LN.checkPermissions(), 8000); info.permission = p?.display || 'unknown' } catch {}
  try { info.exact = await checkExactAlarms() } catch {}
  try { const pend = await withTimeout(LN.getPending(), 8000); info.pending = pend?.notifications?.length || 0 } catch {}
  return info
}

// Ask for notification permission explicitly (Android 13+ needs a user grant).
export async function ensureNotifPermission() {
  const LN = getPlugin()
  if (!LN) return 'n/a'
  try {
    let p = await LN.checkPermissions()
    if (p.display !== 'granted') p = await LN.requestPermissions()
    return p?.display || 'unknown'
  } catch { return 'error' }
}

// Fire a test notification ~5s out so the patient can confirm reminders work
// on their phone without waiting until 9 AM. Returns a result for UI feedback.
export async function sendTestNotification() {
  const LN = getPlugin()
  if (!LN) return { ok: false, reason: 'not-native' }
  const run = async () => {
    let perm = await LN.checkPermissions()
    if (perm.display !== 'granted') perm = await LN.requestPermissions()
    if (perm.display !== 'granted') return { ok: false, reason: 'denied' }
    try { await LN.createChannel({ id: CHANNEL_ID, name: 'Medicine Reminders', description: 'Dose reminders', importance: 4, visibility: 1 }) } catch {}
    // Fire 3 in a burst (~15s apart) so the patient can SEE the repeat behaviour
    // and hear the sound without waiting for a real dose window. Scheduled ONE at
    // a time — same as the real reminders — to prove batch-of-1 works on this OEM.
    const tests = [
      { id: 9997, t: 8000,  n: '1/3', b: '2 more coming ~15s apart to show the repeat.' },
      { id: 9998, t: 23000, n: '2/3', b: 'This is a repeat nudge (like every 15 min).' },
      { id: 9999, t: 38000, n: '3/3', b: 'Last one. Real reminders repeat like this until you mark taken.' },
    ]
    for (const x of tests) {
      try { await LN.schedule({ notifications: [{ id: x.id, title: `🔔 Test reminder ${x.n}`, body: x.b, schedule: { at: new Date(Date.now() + x.t), allowWhileIdle: true }, channelId: CHANNEL_ID, extra: { openReminders: true } }] }) } catch {}
    }
    return { ok: true }
  }
  try { return await withTimeout(run(), 15000) }
  catch (e) { return { ok: false, reason: e?.message === 'timeout' ? 'timeout' : (e?.message || 'error') } }
}

// Tapping a notification opens the Reminders screen. If the dashboard isn't
// mounted (cold start / other tab), navigate there; PatientDashboard picks up
// the flag on mount.
let tapListenerAdded = false
export async function initReminderNotificationTap() {
  if (tapListenerAdded) return
  const LN = await getPlugin()
  if (!LN) return
  tapListenerAdded = true
  try {
    await LN.addListener('localNotificationActionPerformed', () => {
      try { localStorage.setItem(OPEN_FLAG, '1') } catch {}
      if (window.location.pathname.startsWith('/patient-dashboard')) {
        window.dispatchEvent(new CustomEvent('revive-open-reminders'))
      } else {
        window.location.href = '/patient-dashboard'
      }
    })
  } catch {}
}

export function consumeOpenRemindersFlag() {
  try {
    if (localStorage.getItem(OPEN_FLAG) === '1') {
      localStorage.removeItem(OPEN_FLAG)
      return true
    }
  } catch {}
  return false
}
